import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import { DEFAULT_SETTINGS, currencySymbol, unitLabel } from '../utils/orderUnits';

/**
 * AddItemToOrderPopup
 *
 * Add a new SKU (or seedsID for seeds) to an existing order.
 *
 * Props:
 *   orderID        — string
 *   tabKey         — tab slug
 *   materialtypeID — number|null (null for seeds)
 *   year           — default year from the order
 *   month_num      — default month from the order
 *   statuses       — [{ statusID, status_name }]
 *   brands         — [{ brandID, brandName }]   (for filtering products)
 *   settings       — настройки поставщика в заказе { currency, fx_rate, weight_unit, is_import }:
 *                    количество и цена вводятся в его единице и валюте, канон считает сервер
 *   defaults       — { supplierID, supplierName, estDelDate } из заказа. Когда поставщик и дата доставки
 *                    известны, поля периода, поставщика, статуса и дат свёрнуты в одну строку
 *                    (статус OP, дата — последняя из заказа) и раскрываются по «Change»
 *   onClose        — () => void
 *   onSaved        — () => void  (refresh parent after save)
 */
const AddItemToOrderPopup = ({
  orderID,
  tabKey,
  materialtypeID,
  year: defaultYear,
  month_num: defaultMonth,
  statuses,
  brands,
  settings = DEFAULT_SETTINGS,
  defaults = {},
  onClose,
  onSaved,
}) => {
  // Позиция в существующий заказ: поставщик, период, статус и дата берутся из заказа
  const hasDefaults = !!(defaults.supplierID && defaults.estDelDate);
  const isSeeds         = tabKey === 'seeds';
  const isFertilizer    = tabKey === 'fertilizer-orders';
  const isReadyProducts = tabKey === 'ready-products';
  const isWeightTab     = isSeeds || isFertilizer;
  const uLabel          = unitLabel(settings, isWeightTab);
  const sym             = currencySymbol(settings.currency);

  // ── Product search ────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState('');
  const [searchBrand,  setSearchBrand]  = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selected,     setSelected]     = useState(null);  // { sku|seedsID, name }
  const [searching,    setSearching]    = useState(false);
  const searchTimer = useRef(null);

  // ── Order fields ──────────────────────────────────────────────────────────
  const [showDetails,    setShowDetails]    = useState(!hasDefaults);
  const [supplierID,     setSupplierID]     = useState(hasDefaults ? String(defaults.supplierID) : '');
  const [qty,            setQty]            = useState('');
  const [pricePerPcs,    setPricePerPcs]    = useState('');
  const [statusID,       setStatusID]       = useState(hasDefaults ? 'OP' : '');
  const [estDelDate,     setEstDelDate]     = useState(hasDefaults ? defaults.estDelDate : '');
  const [boFollowUpDate, setBoFollowUpDate] = useState('');
  const [tracking,       setTracking]       = useState('');
  const [year,           setYear]           = useState(defaultYear || new Date().getFullYear());
  const [month,          setMonth]          = useState(defaultMonth || new Date().getMonth() + 1);

  // ── Suppliers ─────────────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState([]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        let url;
        if (isFertilizer) {
          url = `/api/fert-suppliers`;
        } else if (isSeeds) {
          url = `/api/seeds_suppliers`;
        } else if (isReadyProducts) {
          url = `/api/material-orders/suppliers/ready-products`;
        } else {
          url = `/api/materialsuppliers_for_type/${materialtypeID}`;
        }
        const { data } = await api.get(url);
        setSuppliers(data || []);
      } catch (e) {
        console.error('Failed to load suppliers', e);
      }
    };
    fetchSuppliers();
  }, [isSeeds, isFertilizer, isReadyProducts, materialtypeID]);

  // ── Product search with debounce ──────────────────────────────────────────
  const doSearch = useCallback(async (q, brandID) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      let url, params;
      if (isFertilizer) {
        url    = `/api/fert-search-materials`;
        params = { search: q };
      } else if (isSeeds) {
        url    = `/api/material-orders/search-seeds`;
        params = { search: q };
      } else if (isReadyProducts) {
        url    = `/api/material-orders/search-ready-products`;
        params = { search: q };
      } else {
        url    = `/api/material-orders/search-products`;
        params = { search: q, materialtypeID, brandID: brandID || '' };
      }
      const { data } = await api.get(url, { params });
      setSearchResults(data || []);
    } catch (e) {
      console.error('Search error', e);
    } finally {
      setSearching(false);
    }
  }, [isSeeds, isFertilizer, isReadyProducts, materialtypeID]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(search, searchBrand), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search, searchBrand, doSearch]);

  const handleSelectItem = (item) => {
    setSelected(item);
    setSearch(item.name);
    setSearchResults([]);
  };

  // ── Validation & submit ───────────────────────────────────────────────────
  const isBO = statusID === 'BO';

  const handleSave = async () => {
    setError('');
    // ошибка в свёрнутом поле — раскрыть поля заказа, чтобы её было где исправить
    const failDetails = (msg) => { setShowDetails(true); setError(msg); };
    if (!selected) { setError('Please select an item'); return; }
    if (!supplierID) { failDetails('Please select a supplier'); return; }
    if (!qty || parseFloat(qty) <= 0) { setError('Please enter a valid quantity'); return; }
    if (!pricePerPcs || parseFloat(pricePerPcs) <= 0) { setError('Please enter a valid price'); return; }
    if (!statusID) { failDetails('Please select a status'); return; }
    if (!isBO && !estDelDate) { failDetails('Please enter estimated delivery date'); return; }
    if (isBO && !boFollowUpDate) { failDetails('Please enter BO follow-up date'); return; }

    setSaving(true);
    try {
      // Количество и цена — как введены (единица и валюта поставщика); канон lb / USD считает сервер
      if (isFertilizer) {
        await api.post(`/api/fert-orders-add-item`, {
          orderID,
          year:              parseInt(year),
          month_num:         parseInt(month),
          rawMaterialID:     selected.rawMaterialID,
          supplierID:        supplierID ? parseInt(supplierID) : null,
          qty_entered:       parseFloat(qty),
          price_entered:     parseFloat(pricePerPcs),
          statusID,
          est_del_date:      isBO ? null : estDelDate,
          bo_follow_up_date: isBO ? boFollowUpDate : null,
          tracking:          tracking || null,
        });
      } else if (isReadyProducts) {
        await api.post(`/api/material-orders/ready-products-add-item`, {
          orderID,
          sku:               selected.sku,
          supplierID:        supplierID ? parseInt(supplierID) : null,
          qty:               parseInt(qty),
          price_entered:     parseFloat(pricePerPcs),
          statusID,
          est_del_date:      isBO ? null : estDelDate,
          bo_follow_up_date: isBO ? boFollowUpDate : null,
          tracking:          tracking || null,
          month_num:         parseInt(month),
          year:              parseInt(year),
        });
      } else {
        await api.post(`/api/material-orders/add-item`, {
          isSeeds,
          orderID,
          year:          parseInt(year),
          month_num:     parseInt(month),
          materialtypeID: isSeeds ? null : materialtypeID,
          sku:           isSeeds ? undefined : selected.sku,
          seedsID:       isSeeds ? selected.seedsID : undefined,
          qty:           isSeeds ? undefined : parseInt(qty),
          qty_entered:   isSeeds ? parseFloat(qty) : undefined,
          price_entered: parseFloat(pricePerPcs),
          statusID,
          est_del_date:      isBO ? null : estDelDate,
          bo_follow_up_date: isBO ? boFollowUpDate : null,
          tracking:      tracking || null,
          supplierID:    parseInt(supplierID),
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const total = (() => {
    const q = parseFloat(qty);
    const p = parseFloat(pricePerPcs);
    return q > 0 && p > 0 ? (q * p).toFixed(2) : '0.00';
  })();

  const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mo-add-overlay" onClick={onClose}>
      <div className="mo-add-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mo-add-header">
          <div>
            <div className="mo-add-title">Add item to order</div>
            <div className="mo-add-subtitle">{orderID}</div>
          </div>
          <button className="mo-add-close" onClick={onClose}>×</button>
        </div>

        <div className="mo-add-body">

          {/* Item search */}
          <div className="mo-add-section-label">
            {isFertilizer ? 'Select raw material' : isSeeds ? 'Select seed' : 'Select product'}
          </div>

          {/* Brand filter for materials (not seeds, not fertilizer) */}
          {!isSeeds && !isFertilizer && brands && brands.length > 0 && (
            <select
              className="mo-add-input"
              value={searchBrand}
              onChange={e => setSearchBrand(e.target.value)}
              style={{ marginBottom: 8 }}
            >
              <option value="">All brands</option>
              {brands.map(b => (
                <option key={b.brandID} value={b.brandID}>{b.brandName || b.brandID}</option>
              ))}
            </select>
          )}

          <div style={{ position: 'relative' }}>
            <input
              className="mo-add-input"
              type="text"
              placeholder={isFertilizer ? 'Search raw material…' : isSeeds ? 'Search seeds by name…' : 'Search by product name…'}
              value={search}
              onChange={e => { setSearch(e.target.value); setSelected(null); }}
              autoFocus
            />
            {searching && (
              <i className="fas fa-spinner fa-spin" style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                color: '#9ba3b0',
              }}></i>
            )}
            {searchResults.length > 0 && (
              <div className="mo-add-dropdown">
                {searchResults.map((item, i) => (
                  <div
                    key={isFertilizer ? item.rawMaterialID : isSeeds ? item.seedsID : `${item.sku}-${i}`}
                    className="mo-add-dropdown-item"
                    onClick={() => handleSelectItem(item)}
                  >
                    <span>{item.name}</span>
                    {item.sku && (
                      <span style={{ fontSize: 11, color: '#9ba3b0', marginLeft: 8 }}>
                        {item.sku}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div className="mo-add-selected">
              <i className="fas fa-check-circle" style={{ color: '#5B965D', marginRight: 6 }}></i>
              {selected.name}
              {selected.sku && <span style={{ color: '#9ba3b0', fontSize: 11, marginLeft: 8 }}>{selected.sku}</span>}
            </div>
          )}

          {/* Year / Month */}
          {showDetails && (
          <div className="mo-add-row">
            <div className="mo-add-field">
              <label className="mo-add-label">Year</label>
              <input
                type="number"
                className="mo-add-input"
                value={year}
                min="2020" max="2099"
                onChange={e => setYear(e.target.value)}
              />
            </div>
            <div className="mo-add-field">
              <label className="mo-add-label">Month</label>
              <select
                className="mo-add-input"
                value={month}
                onChange={e => setMonth(e.target.value)}
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          )}

          {/* Supplier */}
          {showDetails && (
          <div className="mo-add-field">
            <label className="mo-add-label">Supplier *</label>
            <select
              className="mo-add-input"
              value={supplierID}
              onChange={e => setSupplierID(e.target.value)}
            >
              <option value="">Select supplier…</option>
              {suppliers.map(s => (
                <option key={s.supplierID} value={s.supplierID}>{s.supplierName}</option>
              ))}
            </select>
          </div>
          )}

          {/* Qty + Price */}
          <div className="mo-add-row">
            <div className="mo-add-field">
              <label className="mo-add-label">Qty ({uLabel}) *</label>
              <input
                type="number"
                className="mo-add-input"
                value={qty}
                min="0"
                step={isWeightTab ? '0.01' : '1'}
                onChange={e => setQty(e.target.value)}
              />
            </div>
            <div className="mo-add-field">
              <label className="mo-add-label">Price/{uLabel} ({sym}) *</label>
              <input
                type="number"
                className="mo-add-input"
                value={pricePerPcs}
                min="0"
                step="0.0001"
                onChange={e => setPricePerPcs(e.target.value)}
              />
            </div>
          </div>

          {/* Total */}
          {parseFloat(total) > 0 && (
            <div className="mo-add-total">
              Total: <strong>{sym}{parseFloat(total).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
            </div>
          )}

          {/* Поля заказа свёрнуты: поставщик, период, статус и дата доставки взяты из заказа */}
          {!showDetails && (
            <div className="mo-add-total" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1 }}>
                {[
                  defaults.supplierName || suppliers.find(s => String(s.supplierID) === String(supplierID))?.supplierName,
                  `${MONTH_NAMES[parseInt(month) - 1]} ${year}`,
                  'OP, Order Placed',
                  `ETA ${estDelDate.split('-').reverse().join('.')}`,
                ].filter(Boolean).join(' · ')}
              </span>
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                title="Change supplier, period, status or dates for this item"
                style={{ background: 'none', border: 'none', padding: 0, color: '#437045', fontWeight: 600, cursor: 'pointer', fontSize: 'inherit' }}
              >
                Change
              </button>
            </div>
          )}

          {/* Status */}
          {showDetails && (
          <div className="mo-add-field">
            <label className="mo-add-label">Status *</label>
            <select
              className="mo-add-input"
              value={statusID}
              onChange={e => setStatusID(e.target.value)}
            >
              <option value="">Select status…</option>
              {statuses.map(s => (
                <option key={s.statusID} value={s.statusID}>
                  {s.statusID} — {s.status_name}
                </option>
              ))}
            </select>
          </div>
          )}

          {/* Dates */}
          {showDetails && (
          <div className="mo-add-row">
            <div className="mo-add-field">
              <label className="mo-add-label">Est. Delivery {!isBO && '*'}</label>
              <input
                type="date"
                className="mo-add-input"
                value={estDelDate}
                disabled={isBO}
                onChange={e => setEstDelDate(e.target.value)}
              />
            </div>
            <div className="mo-add-field">
              <label className="mo-add-label">BO Follow-up {isBO && '*'}</label>
              <input
                type="date"
                className="mo-add-input"
                value={boFollowUpDate}
                disabled={!isBO}
                onChange={e => setBoFollowUpDate(e.target.value)}
              />
            </div>
          </div>
          )}

          {/* Tracking */}
          <div className="mo-add-field">
            <label className="mo-add-label">Tracking</label>
            <input
              type="text"
              className="mo-add-input"
              value={tracking}
              onChange={e => setTracking(e.target.value)}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="mo-add-error">
              <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mo-add-footer">
          <button className="mo-add-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="mo-add-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving…</> : 'Add item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddItemToOrderPopup;
